import { Tabs as ChakraTabs } from "@chakra-ui/react";
import {
  Children,
  Fragment,
  cloneElement,
  isValidElement,
  type ComponentProps,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
} from "react";
import type {
  BaseTabListProps,
  BaseTabProps,
  BaseTabsSharedProps,
} from "./BaseTabs.props";

export type BaseTabsProps = BaseTabsSharedProps & {
  // Add any web specific props here.
  /**
   * Chakra v1/v2 compatibility: lazy-mount tab panels
   * (Chakra v3 uses `lazyMount`)
   */
  isLazy?: boolean;
  /**
   * Chakra v1/v2 compatibility: manual activation
   * (Chakra v3 uses `activationMode="manual"`)
   */
  isManual?: boolean;
};

function addIndexedValues(children: unknown): ReactNode {
  const indexRef = { current: 0 };

  const mapNode = (node: unknown): ReactNode =>
    Children.map(node as ReactNode, (child) => {
      if (!isValidElement(child)) return child;

      if (child.type === Fragment) {
        return <>{mapNode((child.props as PropsWithChildren).children)}</>;
      }

      const nextValue =
        child.props.value !== undefined
          ? child.props.value
          : String(indexRef.current);
      if (child.props.value === undefined) {
        indexRef.current += 1;
      }

      return cloneElement(child as ReactElement<{ value?: string }>, {
        value: nextValue,
      });
    });

  return mapNode(children);
}

export default function BaseTabs(props: BaseTabsProps): JSX.Element {
  const {
    children,
    index,
    onChange,
    isFitted,
    isLazy,
    isManual,
    onValueChange,
    value,
    defaultValue,
    fitted,
    lazyMount,
    activationMode,
    ...rest
  } = props;

  // Map v1/v2 props to v3 props for backward compatibility
  const fittedValue = fitted !== undefined ? fitted : isFitted;
  const lazyMountValue = lazyMount !== undefined ? lazyMount : isLazy;
  const getActivationModeValue = () => {
    if (activationMode !== undefined) return activationMode;
    if (isManual) return "manual" as const;
    return undefined;
  };
  const activationModeValue = getActivationModeValue();

  // Handle controlled vs uncontrolled value
  const getValueValue = () => {
    if (value !== undefined) return value;
    if (index !== undefined) return String(index);
    return undefined;
  };
  const valueValue = getValueValue();

  // Ensure a tab is selected by default (fix for Chakra v3)
  const valueProps =
    valueValue !== undefined
      ? { value: valueValue }
      : { defaultValue: defaultValue ?? "0" };

  return (
    <ChakraTabs.Root
      {...valueProps}
      fitted={fittedValue}
      lazyMount={lazyMountValue}
      activationMode={activationModeValue}
      onValueChange={(details: { value: string }) => {
        onChange?.(Number(details.value));
        onValueChange?.(details);
      }}
      {...rest}
    >
      {children}
    </ChakraTabs.Root>
  );
}

type ChakraTabsListProps = ComponentProps<typeof ChakraTabs.List>;
type ChakraTabsTriggerProps = ComponentProps<typeof ChakraTabs.Trigger>;
type ChakraTabsContentGroupProps = ComponentProps<
  typeof ChakraTabs.ContentGroup
>;
type ChakraTabsContentProps = ComponentProps<typeof ChakraTabs.Content>;

BaseTabs.TabList = function BaseTabList(
  props: BaseTabListProps & ChakraTabsListProps
) {
  const { children, ...rest } = props;
  return (
    <ChakraTabs.List {...rest}>{addIndexedValues(children)}</ChakraTabs.List>
  );
};

BaseTabs.Tab = function BaseTab(
  props: BaseTabProps &
    Omit<ChakraTabsTriggerProps, "value"> & {
      value?: string;
    }
) {
  const { children, hidden, padding, value: valueProp, ...rest } = props;

  return (
    <ChakraTabs.Trigger
      value={valueProp ?? "0"}
      display={hidden ? "none" : rest.display}
      padding={padding}
      {...rest}
    >
      {children}
    </ChakraTabs.Trigger>
  );
};

BaseTabs.TabPanels = function BaseTabPanels(
  props: ChakraTabsContentGroupProps
) {
  const { children, ...rest } = props;
  return (
    <ChakraTabs.ContentGroup {...rest}>
      {addIndexedValues(children)}
    </ChakraTabs.ContentGroup>
  );
};

BaseTabs.TabPanel = function BaseTabPanel(
  props: Omit<ChakraTabsContentProps, "value"> & { value?: string }
) {
  const { value: valueProp, ...rest } = props;
  return <ChakraTabs.Content value={valueProp ?? "0"} {...rest} />;
};
